import { Add } from "@mui/icons-material";
import { Button, Chip, styled, Typography } from "@mui/material";
import { ReactElement, useState } from "react";

import { FlexBox } from "~frontend/components/FlexBox";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";

import { CreateGifDialog } from "./CreateGifDialog";
import { EditGifDialog } from "./EditGifDialog";
import { GifsTable } from "./GifsTable";
import { PersonalizedGif } from "./types";

const BetaChip = styled(Chip)({
  marginLeft: 5,
  borderRadius: 0,
  backgroundColor: palette.midOrange,
  fontWeight: 600,
  fontSize: "10px",
  padding: 0,
  height: "18px",

  "& .MuiChip-label": {
    padding: "4px",
  },
});

export function PersonalizedGifs(): ReactElement {
  const { subaccountLink } = useSubaccount()!;
  useBreadcrumbs([
    { label: "Personalized GIFs", path: subaccountLink("/personalized-gifs") },
  ]);

  const [createGifDialogOpen, setCreateGifDialogOpen] = useState(false);
  const [editingGif, setEditingGif] = useState<PersonalizedGif | null>(null);

  return (
    <PageContainer>
      <PageHeader>
        Personalized GIFs
        <BetaChip label="Beta" />
      </PageHeader>
      <FlexBox gap="24px" alignItems="flex-end" sx={{ marginBottom: 4 }}>
        <div>
          <Typography variant="h2">
            Use our customizable GIF templates to create GIFs that can be added
            to your campaigns and personalized based on your contact's custom
            fields.
          </Typography>
          <Typography variant="h2" style={{ marginTop: 18 }}>
            <strong>Note: </strong>This feature is free for a limited time,
            while in beta testing.
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ minWidth: "220px" }}
          onClick={() => setCreateGifDialogOpen(true)}
        >
          New Personalized GIF
        </Button>
      </FlexBox>
      <FlexBox
        sx={{
          backgroundColor: "white",
          paddingX: 4,
          paddingBottom: 4,
          paddingTop: 2,
        }}
      >
        <GifsTable onOpenEditDialog={setEditingGif} />
      </FlexBox>
      <CreateGifDialog
        open={createGifDialogOpen}
        onClose={() => setCreateGifDialogOpen(false)}
      />
      {editingGif && (
        <EditGifDialog
          personalizedGif={editingGif}
          onClose={() => setEditingGif(null)}
        />
      )}
    </PageContainer>
  );
}
